import { useCallback, useState } from 'react';
import type { CommandProvider, PaletteMode } from '../types';

/**
 * Parses raw input into a palette mode.
 *
 * - Empty → show recents + pages
 * - ">" prefix → actions
 * - "#keyword " prefix (e.g. "#docs invoice") → scoped to that provider
 * - Anything else → federated search
 */
function parseMode(input: string, providers: CommandProvider[]): PaletteMode {
  const trimmed = input.trim();

  if (!trimmed) {
    return { type: 'empty' };
  }

  if (trimmed.startsWith('>')) {
    return { type: 'action', query: trimmed.slice(1).trimStart() };
  }

  // Check for # prefix
  if (input.startsWith('#')) {
    const afterHash = input.slice(1);

    // Check for exact keyword match (with or without space/query after it)
    if (afterHash.includes(' ')) {
      const spaceIdx = afterHash.indexOf(' ');
      const keyword = afterHash.slice(0, spaceIdx).toLowerCase().trim();
      const query = afterHash.slice(spaceIdx + 1).trim();
      const matchedProvider = providers.find((p) => p.scopeKeyword?.toLowerCase() === keyword);
      if (matchedProvider) {
        return { type: 'scoped', providerId: matchedProvider.id, query };
      }
    } else {
      // No space — check for exact keyword match (immediate scope entry)
      const keyword = afterHash.toLowerCase().trim();
      const exactMatch = providers.find((p) => p.scopeKeyword?.toLowerCase() === keyword);
      if (exactMatch) {
        return { type: 'scoped', providerId: exactMatch.id, query: '' };
      }
      // Partial match — show scope picker
      return { type: 'picking_scope', partial: keyword };
    }
  }

  return { type: 'search', query: trimmed };
}

/**
 * Extracts active scope info from the current mode for badge display.
 */
function getActiveScope(
  mode: PaletteMode,
  providers: CommandProvider[]
): { providerId: string; label: string; keyword: string } | null {
  if (mode.type !== 'scoped') return null;
  const provider = providers.find((p) => p.id === mode.providerId);
  if (!provider) return null;
  return {
    providerId: provider.id,
    label: provider.label,
    keyword: provider.scopeKeyword || '',
  };
}

export interface CommandPaletteState {
  /** Raw input value (includes #keyword prefix when scoped) */
  rawInput: string;
  /** Display input value (without #keyword prefix when scoped) */
  input: string;
  mode: PaletteMode;
  /** Active scope for badge display (null when not scoped) */
  activeScope: { providerId: string; label: string; keyword: string } | null;
  /** Set display input (auto-prefixes with #keyword in scoped mode) */
  setInput: (value: string) => void;
  /** Set raw input directly (for scope picker selection) */
  setRawInput: (value: string) => void;
  /** Clear scope — removes #keyword prefix */
  clearScope: () => void;
  reset: () => void;
}

export function useCommandPaletteState(providers: CommandProvider[]): CommandPaletteState {
  const [rawInput, setRawInput] = useState('');

  const mode = parseMode(rawInput, providers);
  const activeScope = getActiveScope(mode, providers);

  // Display input: strip the #keyword prefix when scoped
  const input = mode.type === 'scoped' ? mode.query : rawInput;

  const setInput = useCallback(
    (value: string) => {
      if (activeScope) {
        // Preserve the #keyword prefix when editing in scoped mode
        setRawInput(`#${activeScope.keyword} ${value}`);
      } else {
        setRawInput(value);
      }
    },
    [activeScope]
  );

  const clearScope = useCallback(() => {
    setRawInput('');
  }, []);

  const reset = useCallback(() => {
    setRawInput('');
  }, []);

  return { rawInput, input, mode, activeScope, setInput, setRawInput, clearScope, reset };
}

export { parseMode };
